import { useState } from 'react';
import { ArrowLeft, Lightbulb, Plus, Trash2 } from 'lucide-react';
import type { Idea, NuevaIdeaDraft, Proyecto } from '../../types';
import { nuevoUUID } from '../../lib/uuid';
import { FormularioIdea } from './FormularioIdea';
import { TarjetaIdea } from './TarjetaIdea';
import { DialogoConfirmacion } from '../ui/DialogoConfirmacion';

interface Props {
  proyecto: Proyecto;
  ideas: Idea[];
  onVolver: () => void;
  onGuardarIdea: (idea: Idea, esCreate: boolean) => void;
  onAlternarHecha: (idea: Idea) => void;
  onEliminarIdea: (id: string) => void;
}

export function VistaProyecto({
  proyecto,
  ideas,
  onVolver,
  onGuardarIdea,
  onAlternarHecha,
  onEliminarIdea,
}: Props) {
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState<Idea | null>(null);
  const [ideaAEliminar, setIdeaAEliminar] = useState<Idea | null>(null);

  const ideasPendientes = ideas.filter((i) => !i.hecha);
  const ideasHechas = ideas.filter((i) => i.hecha);

  const abrirNueva = () => {
    setEditando(null);
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setEditando(null);
  };

  const onGuardar = (draft: NuevaIdeaDraft) => {
    const ahora = new Date().toISOString();
    const idea: Idea = {
      id: editando?.id ?? nuevoUUID(),
      proyectoId: proyecto.id,
      titulo: draft.titulo,
      notas: draft.notas,
      hecha: editando?.hecha ?? false,
      creada: editando?.creada ?? ahora,
      updatedAt: ahora,
      deletedAt: null,
    };
    onGuardarIdea(idea, editando === null);
    cerrarFormulario();
  };

  const confirmarEliminar = () => {
    if (ideaAEliminar) onEliminarIdea(ideaAEliminar.id);
    setIdeaAEliminar(null);
  };

  const renderTarjeta = (idea: Idea) => (
    <TarjetaIdea
      key={idea.id}
      idea={idea}
      onAlternarHecha={onAlternarHecha}
      onEditar={(i) => {
        setEditando(i);
        setMostrarFormulario(true);
      }}
      onEliminar={(i) => setIdeaAEliminar(i)}
    />
  );

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <button
          onClick={onVolver}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ArrowLeft size={16} />
          Proyectos
        </button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2 break-words">
              <Lightbulb className="text-amber-500 shrink-0" size={32} />
              {proyecto.nombre}
            </h1>
            <p className="text-gray-600 mt-1">
              {ideasPendientes.length} pendientes • {ideasHechas.length} hechas
            </p>
          </div>
          <button
            onClick={abrirNueva}
            className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md"
          >
            <Plus size={20} />
            Nueva Idea
          </button>
        </div>
      </div>

      {mostrarFormulario && (
        <FormularioIdea
          key={editando?.id ?? 'nueva'}
          editando={editando}
          onGuardar={onGuardar}
          onCancelar={cerrarFormulario}
        />
      )}

      {ideaAEliminar && (
        <DialogoConfirmacion
          icono={<Trash2 size={24} />}
          titulo="Borrar idea"
          mensaje={`¿Borrar "${ideaAEliminar.titulo}" del proyecto? Se perderán sus anotaciones. Esta acción no se puede deshacer.`}
          textoConfirmar="Sí, borrar"
          iconoBotonConfirmar={<Trash2 size={18} />}
          variante="peligro"
          onConfirmar={confirmarEliminar}
          onCancelar={() => setIdeaAEliminar(null)}
        />
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Pendientes ({ideasPendientes.length})
        </h2>
        {ideasPendientes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500 flex flex-col items-center gap-2">
            <Lightbulb size={32} className="text-gray-300" />
            No hay ideas pendientes en este proyecto
          </div>
        ) : (
          <div className="space-y-4">{ideasPendientes.map(renderTarjeta)}</div>
        )}
      </div>

      {ideasHechas.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-500 mb-4">Hechas ({ideasHechas.length})</h2>
          <div className="space-y-4">{ideasHechas.map(renderTarjeta)}</div>
        </div>
      )}
    </>
  );
}
